// frontend/src/pages/AuditAssessment.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { calculateRobustRisk } from "../lib/RiskEngine";
import { getRelevantQuestions, normalizeJurisdictions } from "../lib/riskQuestionSet";
import computeSeverityFromAnswers from "../lib/severityModel";
import { ShieldAlert, Info } from "lucide-react";

import { appendAuditSnapshot } from "../lib/auditHistory";
import { computePaidReportExposure } from "../lib/paidExposureEngine";
import { getFxRatesPlanning } from "../lib/fxRates";

const PULSE_MS = 650;
const FX_TIMEOUT_MS = 9000;
const FINALIZE_WATCHDOG_MS = 25000;

const STORAGE_KEYS = {
  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",
  CONFIG: "aifine_config",
  ANSWERS_PREFIX: "aifine_answers_business_",
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
  RESULTS_LEGACY: "aifine_results",
  RESULTS_CANONICAL: "aifine_public_audit_results",
  RESULTS_CANONICAL_BUSINESS_PREFIX: "aifine_public_audit_results_business_",
};

// finalize lock keys (prevents double-run on same snapshot id)
const FINALIZE_LOCK_PREFIX = "aifine_finalize_lock_snapshot_";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function clampInt(x, lo, hi) {
  const n = Number(x);
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, Math.floor(n)));
}

function isAnsweredValue(v) {
  return v === true || v === false || v === "unsure" || v === "YES" || v === "NO" || v === "UNSURE";
}

function countAnswered(activeQuestions, answersObj) {
  const q = Array.isArray(activeQuestions) ? activeQuestions : [];
  const a = answersObj && typeof answersObj === "object" ? answersObj : {};
  return q.reduce((acc, item) => {
    const id = item?.id;
    if (!id) return acc;
    return acc + (isAnsweredValue(a[id]) ? 1 : 0);
  }, 0);
}

function findFirstUnansweredIndex(activeQuestions, answersObj) {
  const q = Array.isArray(activeQuestions) ? activeQuestions : [];
  const a = answersObj && typeof answersObj === "object" ? answersObj : {};
  for (let i = 0; i < q.length; i += 1) {
    const id = q[i]?.id;
    if (!id) continue;
    if (!isAnsweredValue(a[id])) return i;
  }
  return 0;
}

function computeAssessmentFromAnswers(answersObj, activeQuestions) {
  const answers = answersObj && typeof answersObj === "object" ? answersObj : {};
  const q = Array.isArray(activeQuestions) ? activeQuestions : [];
  const ids = q.map((x) => x?.id).filter((x) => x != null);

  const failed = [];
  const unsure = [];
  let yesCount = 0;

  ids.forEach((id) => {
    const v = answers[id];
    if (v === true || v === "YES") yesCount += 1;
    else if (v === false || v === "NO") failed.push(id);
    else if (v === "unsure" || v === "UNSURE") unsure.push(id);
  });

  const total = ids.length || 0;
  const pct = total ? Math.round((yesCount / total) * 100) : 0;

  return {
    compliance_score_pct: pct,
    failed_question_ids: failed,
    unsure_question_ids: unsure,
    failed_count: failed.length,
    unsure_count: unsure.length,
    total_questions: total,
    active_question_ids: ids,
  };
}

function withTimeout(promise, timeoutMs, label = "timeout") {
  let t = null;
  const timeout = new Promise((_, reject) => {
    t = window.setTimeout(() => reject(new Error(label)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (t) window.clearTimeout(t);
  });
}

function getActiveBusinessId() {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
  } catch {
    return "";
  }
}

function loadBusinessPayload(businessId) {
  return safeParse(localStorage.getItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
}

function trySetFinalizeLock(snapshotId) {
  try {
    const k = `${FINALIZE_LOCK_PREFIX}${String(snapshotId || "")}`;
    const existing = localStorage.getItem(k);
    if (existing) return false; // already finalized/in-progress
    localStorage.setItem(k, new Date().toISOString());
    return true;
  } catch {
    // if storage fails, still allow finalize; but we also guard with in-memory ref
    return true;
  }
}

export default function AuditAssessment() {
  const navigate = useNavigate();

  /**
   * ✅ Consolidated paywall gating:
   * Auth + paid checks are enforced globally by PaywallGuard (App.jsx).
   * This page should NOT redirect to /auth or /pricing.
   */

  const [businessId, setBusinessId] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [config, setConfig] = useState(null);

  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeNote, setFinalizeNote] = useState("Saving results, generating exposure, and updating Reports…");

  // hard guard to prevent double-finalize within the same mount
  const finalizeInFlightRef = useRef(false);

  const keys = useMemo(() => {
    const bid = businessId || "single_default";
    return {
      answersKey: `${STORAGE_KEYS.ANSWERS_PREFIX}${bid}`,
      progressKey: `${STORAGE_KEYS.PROGRESS_PREFIX}${bid}`,
      canonicalBusinessKey: `${STORAGE_KEYS.RESULTS_CANONICAL_BUSINESS_PREFIX}${bid}`,
    };
  }, [businessId]);

  // Derived (memo) — used in callbacks
  const selectedJurisdictions = useMemo(() => normalizeJurisdictions(config), [config]);
  const activeQuestions = useMemo(() => getRelevantQuestions(config), [config]);
  const total = activeQuestions.length;

  const persistProgress = useCallback(
    (nextIndex, updatedAnswers) => {
      try {
        const completedCount = countAnswered(activeQuestions, updatedAnswers);
        localStorage.setItem(
          keys.progressKey,
          JSON.stringify({
            version: 1,
            business_id: businessId || "single_default",
            currentIndex: clampInt(nextIndex, 0, Math.max(0, total - 1)),
            totalQuestions: total,
            completedCount,
            updated_at_iso: new Date().toISOString(),
          })
        );
      } catch {
        // ignore
      }
    },
    [activeQuestions, businessId, keys.progressKey, total]
  );

  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(keys.progressKey);
    } catch {
      // ignore
    }
  }, [keys.progressKey]);

  useEffect(() => {
    const bid = getActiveBusinessId();
    const resolvedBid = bid || "single_default";
    setBusinessId(resolvedBid);

    const payload = bid ? loadBusinessPayload(bid) : null;
    const fallback = safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null);
    const cfg = payload || fallback;

    if (!cfg) {
      navigate("/start-audit", { replace: true });
      return;
    }
    setConfig(cfg);

    const savedAnswers = safeParse(localStorage.getItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${resolvedBid}`), {});
    if (savedAnswers && typeof savedAnswers === "object") setAnswers(savedAnswers);
    else setAnswers({});
  }, [navigate]);

  // ✅ ESLint-safe: clearProgress is stable and included
  useEffect(() => {
    if (!total) return;

    const answeredCount = countAnswered(activeQuestions, answers);
    const isComplete = total > 0 && answeredCount >= total;

    if (isComplete) {
      clearProgress();
      setCurrentIndex(0);
      return;
    }

    const savedProgress = safeParse(localStorage.getItem(keys.progressKey), null);
    const firstUnanswered = findFirstUnansweredIndex(activeQuestions, answers);

    const hinted =
      savedProgress && typeof savedProgress === "object"
        ? clampInt(savedProgress.currentIndex, 0, Math.max(0, total - 1))
        : null;

    const nextIdx = typeof hinted === "number" ? Math.max(firstUnanswered, hinted) : firstUnanswered;
    setCurrentIndex(nextIdx);
  }, [total, activeQuestions, answers, keys.progressKey, clearProgress]);

  const currentQuestion = useMemo(() => {
    if (!total) return null;
    return activeQuestions[currentIndex];
  }, [activeQuestions, currentIndex, total]);

  const progressPct = useMemo(() => {
    if (!total) return 0;
    return ((currentIndex + 1) / total) * 100;
  }, [currentIndex, total]);

  const currentAnswerValue = useMemo(() => {
    const id = currentQuestion?.id;
    if (!id) return null;
    return answers?.[id] ?? null;
  }, [answers, currentQuestion]);

  const effectiveSelectedAnswer = selectedAnswer != null ? selectedAnswer : currentAnswerValue;

  const gravityTooltip =
    "Gravity is an internal materiality signal (1–10). Higher gravity indicates this control is more likely to be treated as legally significant in enforcement practice. Gravity influences severity scoring, which can affect exposure modeling. It is not a fine amount or legal conclusion.";

  const finalizeAssessment = useCallback(
    async (updatedAnswers) => {
      // in-memory guard
      if (finalizeInFlightRef.current) return;
      finalizeInFlightRef.current = true;

      let watchdogFired = false;

      const watchdog = window.setTimeout(() => {
        watchdogFired = true;
        try {
          window.location.assign("/reports");
        } catch {
          // ignore
        }
      }, FINALIZE_WATCHDOG_MS);

      try {
        setIsFinalizing(true);
        setFinalizeNote("Finalizing: building snapshot…");

        // create snapshot id ONCE
        const snapshot_id = `${Date.now()}`;

        // durable lock (prevents accidental second finalize on same snapshot)
        // If lock already exists, just bail to Reports safely.
        const lockOk = trySetFinalizeLock(snapshot_id);
        if (!lockOk) {
          setFinalizeNote("Snapshot already finalized. Opening Reports…");
          navigate("/reports");
          return;
        }

        const configForEngine = {
          ...(config || null),
          business_id: businessId || "single_default",
          __active_question_ids: activeQuestions.map((q) => q.id),
          __active_questions_total: total,
          __jurisdictions_normalized: selectedJurisdictions,
        };

        const raw = calculateRobustRisk(updatedAnswers, configForEngine);
        const derivedAssessment = computeAssessmentFromAnswers(updatedAnswers, activeQuestions);

        const severity = computeSeverityFromAnswers({
          answers: updatedAnswers,
          activeQuestions,
        });

        const currency_code = configForEngine?.currency_code || "USD";
        const currency_symbol = configForEngine?.currency_symbol || "$";

        const canonical = {
          snapshot_id,
          version: "1.3",
          created_at_iso: new Date().toISOString(),
          source: "detailed_assessment",
          business_id: businessId || "single_default",
          entity_name: configForEngine?.clientName || "Not specified",
          config: configForEngine || null,
          fx: null,

          assessment: {
            compliance_score_pct: derivedAssessment.compliance_score_pct,
            failed_question_ids: derivedAssessment.failed_question_ids,
            unsure_question_ids: derivedAssessment.unsure_question_ids,
            failed_count: derivedAssessment.failed_count,
            unsure_count: derivedAssessment.unsure_count,
            total_questions: derivedAssessment.total_questions,
            active_question_ids: derivedAssessment.active_question_ids,

            severity_tier: severity?.severity_tier,
            severity_score_0_100: severity?.severity_score_0_100,
            severity_explain: severity?.explain,
          },

          summary: {
            currency_code,
            currency_symbol,
            jurisdictions: selectedJurisdictions || [],
            annual_revenue_estimate: Number(configForEngine?.annualTurnover) || 0,
            people_impacted_per_month: Number(configForEngine?.userImpact) || 0,
            compliance_score_pct: derivedAssessment.compliance_score_pct,
            gross_exposure_ceiling_usd: Number(raw?.summary?.gross_exposure_ceiling_usd) || 0,
          },

          engine_output: raw && typeof raw === "object" ? raw : null,
          failedQuestions: derivedAssessment.failed_question_ids,
          unsureQuestions: derivedAssessment.unsure_question_ids,
          score: derivedAssessment.compliance_score_pct,

          inputs_echo: {
            severityTier: severity?.severity_tier,
            severityScore0to100: severity?.severity_score_0_100,
          },

          risk_exposure: null,
          risk_exposure_status: "PENDING",
          risk_exposure_error: null,
        };

        // Persist canonical results (public keys) early so other pages can still see results if something fails mid-way.
        try {
          localStorage.setItem(STORAGE_KEYS.RESULTS_LEGACY, JSON.stringify(canonical));
        } catch {}
        try {
          localStorage.setItem(STORAGE_KEYS.RESULTS_CANONICAL, JSON.stringify(canonical));
        } catch {}
        try {
          localStorage.setItem(keys.canonicalBusinessKey, JSON.stringify(canonical));
        } catch {}

        // FX (optional)
        let fx = null;
        try {
          setFinalizeNote("Fetching FX rates (planning disclosure)…");
          fx = await withTimeout(
            getFxRatesPlanning({ maxAgeHours: 12, forceRefresh: false }),
            FX_TIMEOUT_MS,
            "FX_TIMEOUT"
          );
        } catch {
          fx = null;
        }
        canonical.fx = fx || null;

        // Exposure compute
        try {
          setFinalizeNote("Computing multi-regime exposure…");
          canonical.risk_exposure = computePaidReportExposure({
            configForEngine,
            canonicalPayload: canonical,
          });
          canonical.risk_exposure_status = "OK";

          if (canonical.risk_exposure && canonical.risk_exposure.__error) {
            canonical.risk_exposure_status = "ERROR";
            canonical.risk_exposure_error = String(canonical.risk_exposure.__error);
          }
        } catch (err) {
          canonical.risk_exposure = null;
          canonical.risk_exposure_status = "ERROR";
          canonical.risk_exposure_error = String(err?.message || err || "Exposure error");
        }

        // Persist final canonical (with exposure)
        try {
          localStorage.setItem(STORAGE_KEYS.RESULTS_LEGACY, JSON.stringify(canonical));
        } catch {}
        try {
          localStorage.setItem(STORAGE_KEYS.RESULTS_CANONICAL, JSON.stringify(canonical));
        } catch {}
        try {
          localStorage.setItem(keys.canonicalBusinessKey, JSON.stringify(canonical));
        } catch {}

        // ✅ CRITICAL: Append snapshot history EXACTLY ONCE (after exposure is ready)
        try {
          appendAuditSnapshot(canonical);
        } catch {}

        clearProgress();
        setFinalizeNote("Opening Reports…");

        if (!watchdogFired) {
          navigate("/reports");
          window.setTimeout(() => {
            try {
              if (window.location.pathname !== "/reports") window.location.assign("/reports");
            } catch {}
          }, 450);
        }
      } finally {
        window.clearTimeout(watchdog);
        setIsFinalizing(false);
        finalizeInFlightRef.current = false;
      }
    },
    [
      activeQuestions,
      businessId,
      clearProgress,
      config,
      keys.canonicalBusinessKey,
      navigate,
      selectedJurisdictions,
      total,
    ]
  );

  const beginAnswerFlow = useCallback(
    (answer) => {
      if (!config || !currentQuestion) return;
      if (isAnimating || isFinalizing) return;

      setSelectedAnswer(answer);
      setIsAnimating(true);

      window.setTimeout(() => {
        const updatedAnswers = { ...answers, [currentQuestion.id]: answer };
        setAnswers(updatedAnswers);
        try {
          localStorage.setItem(keys.answersKey, JSON.stringify(updatedAnswers));
        } catch {}

        setSelectedAnswer(null);
        setIsAnimating(false);

        if (currentIndex < total - 1) {
          const nextIndex = currentIndex + 1;
          setCurrentIndex(nextIndex);
          persistProgress(nextIndex, updatedAnswers);
          return;
        }

        finalizeAssessment(updatedAnswers);
      }, PULSE_MS);
    },
    [
      answers,
      config,
      currentIndex,
      currentQuestion,
      finalizeAssessment,
      isAnimating,
      isFinalizing,
      keys.answersKey,
      persistProgress,
      total,
    ]
  );

  const canGoNext = useMemo(() => {
    if (!currentQuestion?.id) return false;
    return isAnsweredValue(answers?.[currentQuestion.id]);
  }, [answers, currentQuestion]);

  const goPrev = useCallback(() => {
    if (isAnimating || isFinalizing) return;
    const nextIndex = Math.max(0, currentIndex - 1);
    setCurrentIndex(nextIndex);
    persistProgress(nextIndex, answers);
  }, [answers, currentIndex, isAnimating, isFinalizing, persistProgress]);

  const goNext = useCallback(() => {
    if (isAnimating || isFinalizing) return;
    if (!canGoNext) return;
    const nextIndex = Math.min(total - 1, currentIndex + 1);
    setCurrentIndex(nextIndex);
    persistProgress(nextIndex, answers);
  }, [answers, canGoNext, currentIndex, isAnimating, isFinalizing, persistProgress, total]);

  const exitToDashboard = useCallback(() => {
    if (isFinalizing) return;
    persistProgress(currentIndex, answers);
    navigate("/dashboard");
  }, [answers, currentIndex, isFinalizing, navigate, persistProgress]);

  if (!config || !currentQuestion) return null;

  const cardBase = "w-full rounded-2xl border p-6 text-left transition-all duration-200 select-none";
  const disabledDim = isAnimating || isFinalizing ? "opacity-50 pointer-events-none" : "";
  const selectedRing = "ring-2 ring-offset-2";
  const pulseClass = "animate-pulse";

  const yesIsSelected = effectiveSelectedAnswer === true || effectiveSelectedAnswer === "YES";
  const noIsSelected = effectiveSelectedAnswer === false || effectiveSelectedAnswer === "NO";
  const unsureIsSelected = effectiveSelectedAnswer === "unsure" || effectiveSelectedAnswer === "UNSURE";

  return (
    <div className="flex h-screen bg-slate-50 font-inter text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 flex flex-col p-8 md:p-12 overflow-auto relative">
        {isFinalizing ? (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-sm text-center">
              <div className="text-sm font-black text-slate-900">Finalizing snapshot…</div>
              <div className="mt-1 text-xs text-slate-600">{finalizeNote}</div>
              <div className="mt-4 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full w-1/2 bg-blue-600 animate-pulse" />
              </div>
              <div className="mt-3 text-[11px] text-slate-500">
                If this takes more than a few seconds, the system will automatically open Reports.
              </div>
            </div>
          </div>
        ) : null}

        <div className="mx-auto w-full max-w-5xl">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Question {currentIndex + 1} of {total}
              </div>
              <div className="mt-1 text-2xl md:text-3xl font-bold tracking-tight">
                {currentQuestion.category || "Assessment"}
              </div>

              {selectedJurisdictions.length ? (
                <div className="mt-2 text-xs text-slate-500">
                  Scope: <span className="font-semibold">{selectedJurisdictions.join(", ")}</span>
                </div>
              ) : null}
            </div>

            <div className="text-right">
              <div className="text-3xl md:text-4xl font-extrabold text-slate-300">{Math.round(progressPct)}%</div>

              <div
                className="mt-1 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1"
                title={gravityTooltip}
              >
                <ShieldAlert size={14} className="text-slate-700" />
                <span className="text-[11px] font-extrabold text-slate-900">
                  Gravity: {currentQuestion.gravity}/10
                </span>
                <span title={gravityTooltip} aria-label="What is gravity?">
                  <Info size={14} className="text-slate-500" />
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-500 ease-out" style={{ width: `${progressPct}%` }} />
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={exitToDashboard}
              disabled={isFinalizing}
              className="text-xs font-semibold text-slate-500 hover:text-slate-900 disabled:opacity-40"
            >
              Exit to Dashboard
            </button>
          </div>
        </div>

        <div className="mx-auto mt-8 w-full max-w-5xl rounded-3xl border border-slate-200 bg-white p-8 md:p-10 shadow-sm">
          <div className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold tracking-widest text-slate-600">
            {currentQuestion.category || "GENERAL"}
          </div>

          <h2 className="mt-4 text-xl md:text-2xl font-bold tracking-tight text-slate-900">{currentQuestion.text}</h2>

          {currentQuestion.guidance && (
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
              <div className="font-semibold text-slate-900 mb-1">Guidance</div>
              <div>{currentQuestion.guidance}</div>
            </div>
          )}

          <div className="mt-8">
            <div className="text-sm font-semibold text-slate-700 mb-3">Select your response:</div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => beginAnswerFlow(true)}
                className={[
                  cardBase,
                  disabledDim,
                  "border-slate-200 bg-white hover:border-slate-300",
                  yesIsSelected ? `${selectedRing} ring-green-500 ${pulseClass}` : "",
                  yesIsSelected ? "bg-green-50 border-green-200" : "",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "mt-1 h-4 w-4 rounded-full border",
                      yesIsSelected ? "border-green-600 bg-green-600" : "border-slate-300 bg-white",
                    ].join(" ")}
                  />
                  <div>
                    <div className="font-semibold text-slate-900">Yes</div>
                    <div className="text-sm text-slate-500">Fully implemented and documented</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => beginAnswerFlow(false)}
                className={[
                  cardBase,
                  disabledDim,
                  "border-slate-200 bg-white hover:border-slate-300",
                  noIsSelected ? `${selectedRing} ring-red-500 ${pulseClass}` : "",
                  noIsSelected ? "bg-red-50 border-red-200" : "",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "mt-1 h-4 w-4 rounded-full border",
                      noIsSelected ? "border-red-600 bg-red-600" : "border-slate-300 bg-white",
                    ].join(" ")}
                  />
                  <div>
                    <div className="font-semibold text-slate-900">No</div>
                    <div className="text-sm text-slate-500">Not currently implemented</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => beginAnswerFlow("unsure")}
                className={[
                  cardBase,
                  disabledDim,
                  "border-slate-200 bg-white hover:border-slate-300",
                  unsureIsSelected ? `${selectedRing} ring-amber-500 ${pulseClass}` : "",
                  unsureIsSelected ? "bg-amber-50 border-amber-200" : "",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "mt-1 h-4 w-4 rounded-full border",
                      unsureIsSelected ? "border-amber-600 bg-amber-600" : "border-slate-300 bg-white",
                    ].join(" ")}
                  />
                  <div>
                    <div className="font-semibold text-slate-900">Unsure</div>
                    <div className="text-sm text-slate-500">Requires further investigation</div>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-8 flex items-center justify-between border-t pt-6">
              <button
                type="button"
                disabled={currentIndex === 0 || isAnimating || isFinalizing}
                onClick={goPrev}
                className="text-sm text-slate-500 hover:text-slate-900 disabled:opacity-40"
              >
                ← Previous Question
              </button>

              <button
                type="button"
                disabled={!canGoNext || isAnimating || isFinalizing}
                onClick={goNext}
                className={[
                  "rounded-xl px-4 py-2 text-sm font-semibold",
                  canGoNext && !isAnimating && !isFinalizing
                    ? "bg-slate-900 text-white hover:bg-slate-800"
                    : "bg-slate-100 text-slate-700 opacity-70 cursor-not-allowed",
                ].join(" ")}
                title={canGoNext ? "Go to next question" : "Select an answer to enable Next"}
              >
                Next Question →
              </button>
            </div>

            <div className="mt-4 text-xs text-slate-500">
              Your progress is saved automatically. You can exit and resume later.
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-5xl mt-10 pb-8 text-xs text-slate-400">
          © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
        </div>
      </main>
    </div>
  );
}